import { HttpClient } from "@angular/common/http";
import {
  AfterViewChecked,
  Component,
  DoCheck,
  HostListener,
  OnDestroy,
  OnInit
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize, take } from "rxjs/operators";
import {
  fixStringLength,
  sportiveCompanyId,
  verifyPhoneNumber,
  pickhobiCompanyId,
  estmarineCompanyId,
  ascsTanitimCompanyId,
  tatilimSensinCompanyId,
  aktaslarLezzetCompanyId,
  pickhobiDiscountCode,
  estmarineDiscountCode,
  boostOxygenCompanyId,
  boostOxygenDiscountCode,
  leoparCompanyId,
  leoparDiscountCode,
  mugoCompanyId,
  muhikuCompanyId,
  muhikuDiscountCode,
  muhikuUtmLink,
  scrollToTop,
  yasomiCompanyId,
  lucidCompanyId,
  peynirciBabaCompanyId,
  kolektifHouseCompanyId,
  medCompanyId
} from "src/app/helpers/helpers";
import { MetaService } from "src/app/services/meta.service";
import { UserService } from "src/app/services/user.service";

@Component({
  selector: "app-survey",
  templateUrl: "./survey.component.html",
  styleUrls: ["./survey.component.scss"]
})
export class SurveyComponent
  implements OnInit, OnDestroy, DoCheck, AfterViewChecked
{
  public static hideLatestSurveyHref = true;

  surveyUrl = "";
  surveyId = "";
  undefinedSurvey = false;
  survey;
  percentage = 0;
  state = "";
  discountCode = "";
  image = null;
  popupState = true;
  time = 0;
  isUserLoggedIn = false;
  userHasPhoneNumber = false;
  surveyFinished = false;
  isUserActive = false;

  phone = "";
  phoneCorrect = false;
  isUserExistsWithPhone = false;
  isUserExistsWithPhoneLoading = false;
  isUserExistsWithPhoneInterval;
  phoneSmsSent = false;
  phonePass = "";
  openConsentAgreement = false;
  ticariIletiAgreement = false;

  _results = [];
  get results() {
    return this._results;
  }
  set results(value: any[]) {
    this._results = value;
    this.persistLatestSurveyResults();
  }

  _otherResults = {};
  get otherResults() {
    return this._otherResults;
  }
  set otherResults(value: any) {
    this._otherResults = value;
    this.persistLatestSurveyOtherResults();
  }

  breadcrumbData = [];

  // These setters won't run when results/otherResults are mutated in-place
  // (e.g. `results[i] = ...`, `results[i].push(...)`, `otherResults[idx].value = ...`).
  // To keep LocalStorage in sync, we detect deep changes and persist when payload changes.
  private _latestSurveyResultsPayloadStr = "";
  private _latestSurveyOtherResultsPayloadStr = "";

  ngDoCheck(): void {
    // Skip in non-browser contexts (defensive) and before router is ready.
    if (typeof window === "undefined" || !this.router?.url) {
      return;
    }

    // Track question count changes for reference
    if (this.survey && this.results.length) {
      const currentQuestionCount = this.getVisibleQuestionCount();
      this.previousQuestionCount = currentQuestionCount;
    }

    this.persistLatestSurveyResultsIfChanged();
    this.persistLatestSurveyOtherResultsIfChanged();
  }

  ngAfterViewChecked(): void {
    // Always scroll to the latest clicked question after view updates
    if (
      this.shouldScrollToQuestion &&
      typeof window !== "undefined" &&
      this.questionIndexToScrollTo >= 0
    ) {
      // Clear any pending timeout
      if (this.scrollRestoreTimeout) {
        clearTimeout(this.scrollRestoreTimeout);
      }

      // Use requestAnimationFrame + setTimeout to ensure DOM has fully updated and rendered
      requestAnimationFrame(() => {
        this.scrollRestoreTimeout = setTimeout(() => {
          this.scrollToQuestion(this.questionIndexToScrollTo);
          // Don't reset flags here - let them be reset by the multiple attempts in saveQuestionIndexForScroll
        }, 200); // Delay to ensure DOM is fully rendered, especially when new questions appear
      });
    }
  }

  private scrollToQuestion(questionIndex: number): void {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    // Try multiple methods to find the question element
    let questionElement: HTMLElement | null = null;

    // Method 1: Try getElementById with the ID format from template
    const idToFind = `question-${questionIndex}`;
    questionElement = document.getElementById(idToFind);

    // Method 2: Try querySelector with attribute selector (handles Angular's attribute binding)
    if (!questionElement) {
      questionElement = document.querySelector(
        `[id="${idToFind}"]`
      ) as HTMLElement;
    }

    // Method 3: Try finding all question divs within questions-holder (not user-info-holder)
    if (!questionElement) {
      const questionsHolder = document.querySelector(
        ".questions-holder:not(.user-info-holder)"
      );
      if (questionsHolder) {
        const allQuestions = Array.from(
          questionsHolder.querySelectorAll(".question")
        ) as HTMLElement[];
        if (allQuestions[questionIndex]) {
          questionElement = allQuestions[questionIndex];
        }
      }
    }

    if (questionElement && window.innerWidth > 468) {
      // Use scrollIntoView which is more reliable than manual scroll calculation
      questionElement.scrollIntoView({
        behavior: "smooth",
        block: "center", // Center the element in the viewport
        inline: "nearest"
      });
    }
  }

  private getVisibleQuestionCount(): number {
    if (!this.survey || !this.survey.questions) {
      return 0;
    }
    return this.survey.questions.filter((q, index) => {
      if (q.hide) {
        return false;
      }
      if (q.isConnected === "true") {
        if (q.connectionOptions && q.connectionOptions.length) {
          return this.isAvailableToShow(
            "multiple",
            q.connectedQuestionIndex,
            q.connectionOptions,
            undefined,
            q.kind,
            q.questionNumber,
            q.order
          );
        } else if (q.minimumValue) {
          return this.isAvailableToShow(
            "ranged",
            q.connectedQuestionIndex,
            q.minimumValue,
            q.maximumValue,
            q.kind,
            q.questionNumber,
            q.order
          );
        }
        return false;
      }
      return true;
    }).length;
  }

  private saveQuestionIndexForScroll(questionIndex: number): void {
    // Clear any existing scroll timeouts
    this.scrollTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.scrollTimeouts = [];

    // Save the question index and trigger scroll after view updates
    this.questionIndexToScrollTo = questionIndex;
    this.shouldScrollToQuestion = true;

    // Try scrolling multiple times with increasing delays to handle DOM updates
    if (typeof window !== "undefined") {
      // First attempt after short delay
      const timeout1 = setTimeout(() => {
        this.scrollToQuestion(questionIndex);
      }, 100);
      this.scrollTimeouts.push(timeout1);

      // Second attempt after longer delay (for slower DOM updates)
      const timeout2 = setTimeout(() => {
        this.scrollToQuestion(questionIndex);
      }, 300);
      this.scrollTimeouts.push(timeout2);

      // Third attempt after even longer delay (for conditional rendering)
      const timeout3 = setTimeout(() => {
        this.scrollToQuestion(questionIndex);
      }, 500);
      this.scrollTimeouts.push(timeout3);
    }
  }

  private persistLatestSurveyResultsIfChanged() {
    try {
      const payloadStr = JSON.stringify({
        href: this.router.url,
        results: this._results
      });
      if (payloadStr === this._latestSurveyResultsPayloadStr) {
        return;
      }
      this._latestSurveyResultsPayloadStr = payloadStr;
      window.localStorage.setItem("latestSurveyResults", payloadStr);
    } catch (e) {
      // Ignore serialization/localStorage failures (e.g. quota exceeded).
    }
  }

  private persistLatestSurveyOtherResultsIfChanged() {
    try {
      const payloadStr = JSON.stringify({
        href: this.router.url,
        otherResults: this._otherResults
      });
      if (payloadStr === this._latestSurveyOtherResultsPayloadStr) {
        return;
      }
      this._latestSurveyOtherResultsPayloadStr = payloadStr;
      window.localStorage.setItem("latestSurveyOtherResults", payloadStr);
    } catch (e) {
      // Ignore serialization/localStorage failures (e.g. quota exceeded).
    }
  }

  private persistLatestSurveyResults() {
    // Force write even if called by setter: payload cache makes it cheap when unchanged.
    this.persistLatestSurveyResultsIfChanged();
  }

  private persistLatestSurveyOtherResults() {
    // Force write even if called by setter: payload cache makes it cheap when unchanged.
    this.persistLatestSurveyOtherResultsIfChanged();
  }

  get isRegisterButton() {
    return (
      !this.isUserLoggedIn && !this.isUserExistsWithPhone && this.phoneCorrect
    );
  }

  verifySentDate = new Date();
  verificationSmsSecond = 0;
  verificationInterval;
  verificationSending = false;

  showResetPassword = false;

  finishSurveyWorking = false;

  productLinksModalShowing = false;

  landingPageKey = "";
  giveStatToPazanova = false;

  // Scroll position tracking
  private previousQuestionCount = 0;
  private shouldScrollToQuestion = false;
  private questionIndexToScrollTo = -1;
  private scrollRestoreTimeout: any;
  private scrollTimeouts: any[] = [];

  get resendCodeText() {
    if (this.verificationSmsSecond >= 0) {
      return `${this.verificationSmsSecond}sn. - Şifreyi yeniden gönder`;
    }
    return "Şifreyi yeniden gönder";
  }

  get discountEarningDesc() {
    let discountDesc = "";
    if (!this.survey) {
      return discountDesc;
    }

    if (this.survey.discountType === "Indirim") {
      discountDesc = `${this.survey.discountAmount} ${this.survey.discountAmountType} İndirim`;
    } else {
      discountDesc = `${this.survey.discountDescription?.trim() || ""}`;
    }
    return discountDesc;
  }

  get discountCodeTitleText() {
    if (this.survey?.companyId === estmarineCompanyId) {
      return "Tebrikler, anketiniz başarıyla tamamlandı ve kampanya açıklamamızda belirttiğimiz seçili ürünlerde 15 %’e varan indirim kazandınız. 🙂";
    }

    return `Tebrikler, anketiniz başarıyla tamamlandı ve ${
      this.survey?.company?.name || "..."
    } markasından ${this.discountEarningDesc} kazandınız. 🙂`;
  }

  get discountCodeDescText() {
    let discountTypeText = "";
    switch (this.survey?.discountType) {
      case "Hediye":
        discountTypeText = "Hediye";
        break;
      case "Indirim":
        discountTypeText = "İndirim";
        break;
      default:
        discountTypeText = this.survey?.discountType.toString();
    }

    if (this.survey?.companyId === ascsTanitimCompanyId) {
      return "İşte kodunuz: ";
    }
    if (this.survey?.companyId === sportiveCompanyId) {
      return `İndirim kodunuzu <a href="https://www.sportive.com.tr" target="_blank">www.sportive.com.tr</a> ve Sportive Mobil Uygulamasında kullanabilirsiniz. İndirim kodunuzu sepeti onaylamadan "İndirim Kodunuz Var Mı?" yazan alana girebilirsiniz. <br/> İşte ${discountTypeText} kodunuz: `;
    }
    if (this.survey?.companyId === pickhobiCompanyId) {
      return `İndirim kodunuzu <a href="https://www.pickhobi.com" target="_blank">www.pickhobi.com</a> web sitesinde kullanabilirsiniz. İndirim kodunuzu sepeti onaylamadan "Kupon Kullan" yazan alana girebilirsiniz. <br/> İşte ${discountTypeText} kodunuz: `;
    }
    if (this.survey?.companyId === estmarineCompanyId) {
      return `İndirim kodunuzu tüm Eastmarine mağazalarında kullanabilirsiniz. <br/> İşte ${discountTypeText} kodunuz: `;
    }
    if (this.survey?.companyId === tatilimSensinCompanyId) {
      return `Hediye puanınızı <a href="https://www.tatilimsensin.com" target="_blank">www.tatilimsensin.com</a> ve Tatilimsensin Mobil uygulamasında kullanabilirsiniz. Hediye puanını kullanmak için bizimle paylaştığınız telefon numarası ile Tatilimsensin’e üye olmanız yeterlidir. Hediye puanı üye olduktan sonra 48 saat içinde hesabınıza yüklenecektir.`;
    }
    if (this.survey?.companyId === aktaslarLezzetCompanyId) {
      return `Hediyenizi kullanmak için Nelipide Gurme yetkilisine size özel indirim kodunu söylemeniz yeterlidir. İşte ${discountTypeText} kodunuz: <b>${this.discountCode}</b> <br /> Adres: Göztepe Mah. Bağdat Cad. No:199/B Kadıköy/İSTANBUL Tel: 0 216 356 52 52`;
    }
    if (this.survey?.companyId === muhikuCompanyId) {
      return `İndirim kodunuzu Muhiku web sitesinde (<a href="${muhikuUtmLink}" target="_blank">www.muhiku.com</a>) kullanabilirsiniz. Kampanyadan yararlanmak için indirim kodunuzu, Muhiku.com’da ödeme sayfasında yer alan ‘’Kupon Kodu’’ alanına yazmanız yeterlidir. İşte İndirim kodunuz: `;
    }
    if (this.survey?.companyId === mugoCompanyId) {
      return `İndirim kodunuzu Mugo web sitesinde (<a href="https://mugo.com.tr" target="_blank">www.mugo.com.tr</a>) kullanabilirsiniz. Kampanyadan yararlanmak için indirim kodunuzu, Mugo.com.tr’de ödeme sayfasında yer alan ‘’İndirim kodu alanına yazmanız yeterlidir. İşte İndirim kodunuz: “<b>${this.discountCode}</b>”`;
    }

    return `İşte ${discountTypeText} kodunuz: `;
  }

  get showDiscountCode() {
    return (
      this.survey?.companyId !== tatilimSensinCompanyId &&
      this.survey?.companyId !== aktaslarLezzetCompanyId &&
      this.survey?.companyId !== mugoCompanyId
    );
  }

  get showDiscountQr() {
    return this.image && this.survey?.companyId === peynirciBabaCompanyId;
  }

  get customGiftMessage() {
    if (this.survey?.customGiftText) {
      let discountDesc = "";
      if (this.survey.discountType === "Indirim") {
        discountDesc = `${this.survey.discountAmount} ${this.survey.discountAmountType} İndirim`;
      } else {
        discountDesc = `${this.survey.discountDescription?.trim() || ""}`;
      }

      return this.survey.customGiftText
        .replace(/\[discountCode]/g, this.discountCode)
        .replace(/\[discountDescription]/g, discountDesc);
    }
    return `${this.discountCodeDescText}${
      this.showDiscountCode ? ` ${this.discountCode}` : ""
    }`;
  }

  get showProductsLinks() {
    return (
      this.survey?.companyId === yasomiCompanyId ||
      this.survey?.companyId === lucidCompanyId
    );
  }

  get showLpTitle() {
    return !!this.landingPageKey;
  }

  get showLpLogo() {
    return (
      !!this.landingPageKey && !this.isHummel && !this.isEker && !this.isTeqsma
    );
  }

  get showLpTopLeft() {
    return !!this.landingPageKey && !this.isHummel && !this.isTeqsma;
  }

  get showLpTopRight() {
    return (
      !!this.landingPageKey && !this.isHummel && !this.isEker && !this.isTeqsma
    );
  }

  get showLpBottomLeft() {
    return (
      !!this.landingPageKey &&
      !this.isHummel &&
      !this.isLucid &&
      !this.isKolektifHouse
    );
  }

  get showLpBottomRight() {
    return (
      !!this.landingPageKey &&
      !this.isHummel &&
      !this.isLucid &&
      !this.isKolektifHouse
    );
  }

  get lpTopLeftSrc() {
    if (this.isPeynirciSerdivan) {
      return `/assets/lp/${this.landingPageKey}/top-right.png`;
    }
    return `/assets/lp/${this.landingPageKey}/top-left.png`;
  }

  get lpTopRightSrc() {
    if (this.landingPageKey === "sportive") {
      return "/assets/lp/sportive/top-right-ball.png";
    }
    return `/assets/lp/${this.landingPageKey}/top-right.png`;
  }

  get showLpBorder() {
    return this.landingPageKey === "boost";
  }

  get isHummel() {
    return (
      this.landingPageKey === "hummel" || this.landingPageKey === "hummel-2"
    );
  }

  get isHummel2() {
    return this.landingPageKey === "hummel-2";
  }

  get isEker() {
    return this.landingPageKey === "eker";
  }

  get showLpMiddleRight() {
    return this.isEker;
  }

  get isLucid() {
    return this.landingPageKey === "lucid";
  }

  get isPeynirci() {
    return this.landingPageKey === "peynirci";
  }

  get isPeynirciSerdivan() {
    return this.isPeynirci && this.surveyUrl.includes("serdivan");
  }

  get titleSrc() {
    if (this.isTeqsma) {
      return `/assets/lp/teqsma/teqsma-top.png`;
    }
    if (
      this.isLucid ||
      this.isPeynirci ||
      this.isKolektifHouse ||
      this.isMed ||
      this.isNesPilates
    ) {
      return `/assets/lp/${this.landingPageKey}/title.png`;
    }
    return `/assets/lp/${this.landingPageKey}/title.svg`;
  }

  get titleMobileSrc() {
    if (this.isTeqsma) {
      return `/assets/lp/teqsma/teqsma-top.png`;
    }
    if (
      this.isLucid ||
      this.isPeynirci ||
      this.isKolektifHouse ||
      this.isMed ||
      this.isNesPilates
    ) {
      return `/assets/lp/${this.landingPageKey}/title-mobile.png`;
    }
    return `/assets/lp/${this.landingPageKey}/title-mobile.svg`;
  }

  get isKolektifHouse() {
    return this.survey?.companyId === kolektifHouseCompanyId;
  }

  get isMed() {
    return this.survey?.companyId === medCompanyId;
  }

  get isNesPilates() {
    return this.landingPageKey === "nespilates";
  }

  get isTeqsma() {
    return this.landingPageKey === "teqsma";
  }

  resultsStr = "";
  otherResultsStr = "";

  constructor(
    private _sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private http: HttpClient,
    private userService: UserService,
    private metaService: MetaService,
    private router: Router
  ) {
    this.timeCounter();
  }

  ngOnInit(): void {
    this.resultsStr = window.localStorage.getItem("latestSurveyResults");
    this.otherResultsStr = window.localStorage.getItem(
      "latestSurveyOtherResults"
    );

    scrollToTop();
    this.getSurvey();
    this.userService.user.subscribe((res) => {
      this.isUserLoggedIn = !!res;
      this.userHasPhoneNumber = !!res?.phone;
      this.isUserActive = !!res?.isActive;
    });
  }

  ngOnDestroy(): void {
    if (this.verificationInterval) {
      clearInterval(this.verificationInterval);
    }

    if (this.scrollRestoreTimeout) {
      clearTimeout(this.scrollRestoreTimeout);
    }

    // Clear all scroll timeouts
    this.scrollTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.scrollTimeouts = [];

    if (this.landingPageKey) {
      document.body.classList.remove(this.landingPageKey);
    }
  }

  trackPageView() {
    if (this.landingPageKey && this.giveStatToPazanova) {
      this.http
        .post("/pazanova/track-page-view", {
          landingPageKey: this.landingPageKey,
          surveyId: this.surveyId
        })
        .pipe(take(1))
        .subscribe(() => {});
    }
  }

  @HostListener("document:keypress", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.finishSurvey();
    }
  }

  timeCounter() {
    setInterval(() => {
      this.time++;
    }, 1000);
  }

  isAvailableToShow(
    type,
    relatedQuestionIndex,
    connectionOptions,
    maximumValue,
    kind,
    questionNumber,
    order
  ) {
    if (kind == "open") {
      if (questionNumber?.endsWith(".D")) {
        return false;
      }

      const optText = this.survey.questions[
        relatedQuestionIndex
      ]?.question?.options?.find(
        (m) => m.value == this.results[relatedQuestionIndex]
      )?.text;
      const isOther = optText === "Diğer" || optText === "Belirtiniz!";
      if (isOther) {
        return false;
      }
    }

    let output = false;
    if (type == "multiple") {
      if (
        typeof this.results[relatedQuestionIndex] == "string" ||
        typeof this.results[relatedQuestionIndex] == "number"
      ) {
        output =
          connectionOptions.find(
            (x) => this.results[relatedQuestionIndex] == x
          ) > -1;
      } else if (this.results[relatedQuestionIndex]?.indexOf) {
        output =
          connectionOptions.find(
            (x) => this.results[relatedQuestionIndex].indexOf(x) > -1
          ) > -1;
      }

      if (this.isKolektifHouse && output && order === 25) {
        if (
          Array.isArray(this.results[relatedQuestionIndex]) &&
          this.results[relatedQuestionIndex].length <= 1
        ) {
          output = false;
        }
      }

      if (this.isKolektifHouse && output && order === 15) {
        const kolektifHouseOptionValue = 9;
        if (
          Array.isArray(this.results[relatedQuestionIndex]) &&
          this.results[relatedQuestionIndex].includes(kolektifHouseOptionValue)
        ) {
          output = false;
        }
        // Q15 "Kolektif House'ı daha önce duydunuz mu?" should NOT show when
        // participant selected "Kolektif House" in Q11 (first brand that comes to mind)
        const q11Index = this.survey.questions?.findIndex(
          (q) => q.order === 11
        );
        const kolektifHouseInQ11Value = 8;
        if (
          q11Index >= 0 &&
          Array.isArray(this.results[q11Index]) &&
          this.results[q11Index].includes(kolektifHouseInQ11Value)
        ) {
          output = false;
        }
      }

      if (this.isKolektifHouse && output && order === 16) {
        const kolektifHouseOptionValue = [1, 9];
        if (
          Array.isArray(this.results[relatedQuestionIndex]) &&
          this.results[relatedQuestionIndex].some((m) =>
            kolektifHouseOptionValue.includes(m)
          )
        ) {
          output = false;
        }
      }
    } else {
      if (typeof maximumValue === "undefined") {
        if (this.results[relatedQuestionIndex] >= connectionOptions) {
          output = true;
        }
      } else {
        if (
          this.results[relatedQuestionIndex] >= connectionOptions &&
          this.results[relatedQuestionIndex] <= maximumValue
        ) {
          output = true;
        }
      }
    }

    if (output) {
      const parent = this.survey.questions[relatedQuestionIndex];
      if (parent.isConnected == "true") {
        const isParentShowing = this.isAvailableToShow(
          "multiple",
          parent.connectedQuestionIndex,
          parent.connectionOptions,
          undefined,
          parent.kind,
          parent.questionNumber,
          parent.order
        );
        if (!isParentShowing) {
          output = false;
        }
      }
    }

    return output;
  }

  getSurvey() {
    this.surveyUrl = this.route.snapshot.paramMap.get("id");
    const companyUrl = this.route.snapshot.paramMap.get("companyUrl");
    this.http
      .post("/getSurvey", {
        id: this.surveyUrl,
        companyUrl: companyUrl
      })
      .pipe(take(1))
      .subscribe((res: any) => {
        const surveyDataStr = window.localStorage.getItem("latestSurveyHref");
        let setNewSurveyData = true;
        if (surveyDataStr) {
          try {
            const surveyData = JSON.parse(surveyDataStr);
            setNewSurveyData = surveyData.href !== this.router.url;
          } catch (e) {
            setNewSurveyData = surveyDataStr !== this.router.url;
          }
        }

        if (setNewSurveyData) {
          const surveyData = {
            href: this.router.url,
            createdAt: new Date().toISOString(),
            clickCount: 0
          };
          window.localStorage.setItem(
            "latestSurveyHref",
            JSON.stringify(surveyData)
          );
        }

        this.survey = res;
        this.surveyId = res._id?.toString() || "";
        this.undefinedSurvey = !!res.undefinedSurvey;
        this.giveStatToPazanova = !!res.giveStatToPazanova;
        this.breadcrumbData = [{ name: "Anasayfa", href: "/" }];
        if (res.company?.name) {
          this.breadcrumbData.push({
            name: fixStringLength(res.company.name, 50),
            href: `/marka/${res.company.slugUrl || res.company._id}/${
              this.surveyUrl
            }`
          });
        }
        this.breadcrumbData.push({
          name: fixStringLength(res.name, 50),
          active: true
        });

        for (const q of res.questions) {
          if (q.valueCount > 1) {
            this.results.push([]);
          } else {
            this.results.push("");
          }

          if (q.sort === "multiple" && q.question.options?.length) {
            for (let i = 0; i < q.question.options.length; i++) {
              q.question.options[i].realIndex = i;
            }
            if (q.shuffleOptions) {
              q.question.options = q.question.options.sort(
                () => Math.random() - 0.5
              );
            }
          }
        }

        if (res.lpKey) {
          this.landingPageKey = res.lpKey;
          if (this.isKolektifHouse) {
            this.landingPageKey = "";
          }
          document.body.classList.add(this.landingPageKey);
          this.trackPageView();
        }

        if (this.resultsStr) {
          const results = JSON.parse(this.resultsStr);
          if (
            results.href === this.router.url &&
            this.results.length === results.results.length
          ) {
            this.results = results.results;
            this;
          }
        }

        if (this.otherResultsStr) {
          const otherResults = JSON.parse(this.otherResultsStr);
          if (otherResults.href === this.router.url) {
            this.otherResults = otherResults.otherResults;
          }
        }

        this.setMeta();
      });
  }

  setMeta() {
    let disc = "";
    if (this.survey.discountType !== "Hediye") {
      disc = `${this.survey.discountAmount} ${this.survey.discountAmountType} indirim`;
    } else if (this.survey.discountType === "Hediye") {
      disc = this.survey.discountDescription?.trim();
    }

    const companyName = this.survey.company?.name || "";
    this.metaService.set({
      title: `${companyName} şirketinin anketini doldur ${disc} kazan`,
      description: `${companyName} kampanya fırsatını kaçırmayın. ${companyName} anketine katıl ${disc} Kazan.`
    });
  }

  finishSurvey() {
    if (this.survey.timeLimit > this.time) {
      this.popupState = false;
      this.state = "not_valid";
      return false;
    }

    if (!this.validateSurvey()) {
      this.popupState = false;
      this.state = "not_valid";
      return false;
    }

    if (!this.isUserLoggedIn || !this.userHasPhoneNumber) {
      if (!this.phoneCorrect) {
        verifyPhoneNumber(this.phone, true);
        return false;
      }
      if (!this.isRegisterButton && !this.phonePass) {
        alert("Lütfen Şifre alanını doldurunuz!");
        return false;
      }
      if (this.isRegisterButton && !this.phonePass) {
        this.state = "verification";
        this.sendVerifyCodeToPhone();
        return false;
      }
    }

    this.surveyFinished = true;
    this.finishSurveyWorking = true;
    const utmLink = this.route.snapshot.queryParamMap.get("utm_link") || "";

    this.http
      .post("/saveSurveyResult", {
        landingPageKey: this.landingPageKey,
        giveStatToPazanova: this.giveStatToPazanova,
        utmLink: utmLink,
        finishTimeInSeconds: this.time,
        surveyId: this.surveyId,
        answers: this.results.map((m, index) => {
          if (this.otherResults[index]?.selected) {
            return this.otherResults[index].value;
          }
          return m;
        }),
        newUser:
          this.isUserLoggedIn && this.userHasPhoneNumber
            ? null
            : {
                phone: this.phone,
                password: this.phonePass,
                openConsentAgreement: this.openConsentAgreement,
                ticariIletiAgreement: this.ticariIletiAgreement
              }
      })
      .pipe(
        take(1),
        finalize(() => {
          this.finishSurveyWorking = false;
        })
      )
      .subscribe((saveRes: any) => {
        if (typeof saveRes.data === "string") {
          alert(saveRes.data);
        } else {
          this.trackMeta();
          window.localStorage.removeItem("latestSurveyHref");
          window.localStorage.removeItem("latestSurveyResults");
          window.localStorage.removeItem("latestSurveyOtherResults");

          if (this.survey.companyId === pickhobiCompanyId) {
            this.discountCode = pickhobiDiscountCode;
          } else if (this.survey.companyId === estmarineCompanyId) {
            this.discountCode = estmarineDiscountCode;
          } else if (this.survey.companyId === boostOxygenCompanyId) {
            this.discountCode = boostOxygenDiscountCode;
          } else if (this.survey.companyId === leoparCompanyId) {
            this.discountCode = leoparDiscountCode;
          } else if (this.survey.companyId === muhikuCompanyId) {
            this.discountCode = muhikuDiscountCode;
          } else {
            this.discountCode = saveRes.data.discountCode;
          }
          if (saveRes.data.user && saveRes.data.token) {
            this.userService.saveLoginResponse(saveRes.data);
          }

          if (this.landingPageKey) {
            this.navigateToThanksPage(saveRes);
          } else {
            this.getQr(saveRes.data.earningId);
          }
        }
      });
  }

  validateSurvey() {
    let result = true;
    this.survey.questions
      .filter((m) => !m.hide)
      .forEach((question, index) => {
        if (question.isRelated == "true") {
          let relatedQuestionAnswer = Number(
            this.results[question.relatedQuestionIndex]
          );
          let currentQuestionAnswer = Number(this.results[index]);
          if (currentQuestionAnswer > relatedQuestionAnswer) {
            if (
              currentQuestionAnswer - relatedQuestionAnswer >=
              Number(question.relatedQuestionError)
            ) {
              result = false;
            }
          } else {
            if (
              relatedQuestionAnswer - currentQuestionAnswer >=
              Number(question.relatedQuestionError)
            ) {
              result = false;
            }
          }
        }
      });
    return result;
  }

  getBackground() {
    return this._sanitizer.bypassSecurityTrustStyle(`url(${this.image})`);
  }

  returnHome() {
    this.state = "";
    this.router.navigateByUrl("/");
  }

  handleCheckboxSelect(index, optionValue, valueCount) {
    // Save question index to scroll to after new questions appear
    this.saveQuestionIndexForScroll(index);

    if (!this.results[index]) {
      this.results[index] = [];
      this.results[index].push(optionValue);
    } else {
      const optionIndex = this.results[index].indexOf(optionValue);
      if (optionIndex > -1) {
        this.results[index].splice(optionIndex, 1);
      } else {
        if (this.results[index].length >= parseInt(valueCount) + 1) {
          this.results[index].shift();
          this.results[index].push(optionValue);
        } else {
          this.results[index].push(optionValue);
        }
      }
    }
  }

  handleRadioSelect(index, optionValue) {
    // Save question index to scroll to after new questions appear
    this.saveQuestionIndexForScroll(index);
    this.results[index] = optionValue;
  }

  setMultipleRangeResult(questionIndex, optionIndex, rangeValue) {
    // Save question index to scroll to after new questions appear
    this.saveQuestionIndexForScroll(questionIndex);

    if (!this.results[questionIndex]) {
      this.results[questionIndex] = {};
    }
    this.results[questionIndex][optionIndex] = rangeValue;
  }

  getMultipleRangeResult(questionIndex, optionIndex) {
    if (
      this.results[questionIndex] &&
      typeof this.results[questionIndex] === "object"
    ) {
      return this.results[questionIndex][optionIndex];
    }
    return null;
  }

  isMultipleRangeOptionSelected(questionIndex, optionIndex, rangeOptions) {
    if (!this.results[questionIndex]) {
      return false;
    }
    for (const rangeOpt of rangeOptions) {
      if (
        this.getMultipleRangeResult(questionIndex, optionIndex) ===
        rangeOpt.value
      ) {
        return true;
      }
    }
    return false;
  }

  toggleMultipleRangeOption(questionIndex, optionIndex, option) {
    // Save question index to scroll to after new questions appear
    this.saveQuestionIndexForScroll(questionIndex);

    if (!this.results[questionIndex]) {
      this.results[questionIndex] = {};
    }

    // Eğer seçili ise, kaldır (derece değerini de sil)
    if (this.results[questionIndex].hasOwnProperty(optionIndex)) {
      delete this.results[questionIndex][optionIndex];
    } else {
      // Seçili değilse, null olarak işaretle (derece henüz seçilmemiş)
      this.results[questionIndex][optionIndex] = null;
    }
  }

  reSendForm(user): any {
    if (user) {
      this.isUserLoggedIn = true;
      if (user.phone) {
        this.userHasPhoneNumber = true;
      }
      if (user.isActive) {
        this.isUserActive = true;
      }
    }
    this.finishSurvey();
  }

  getQr(earningId: string) {
    this.http
      .post("/getQR", {
        id: `E=${earningId || ""}`
      })
      .pipe(take(1))
      .subscribe((res: any) => {
        this.image = res.qr;
        if (!this.landingPageKey) {
          this.state = "finished";
        }
      });
  }

  onPhoneChange() {
    this.phoneCorrect = verifyPhoneNumber(this.phone, false);
    if (!this.phoneCorrect) {
      return;
    }

    this.isUserExistsWithPhoneLoading = true;
    this.http
      .get(`/is-phone-exists?phone=${this.phone}`)
      .pipe(
        take(1),
        finalize(() => (this.isUserExistsWithPhoneLoading = false))
      )
      .subscribe((res: any) => {
        this.isUserExistsWithPhone = !!res.userExists;
        if (this.isUserExistsWithPhone && this.isUserLoggedIn) {
          alert(
            "Telefon numarası başka bir hesaba kayıtlı, lütfen telefon numarasıyla giriş yapınız."
          );
        }
      });
  }

  trackMeta() {
    try {
      if (this.landingPageKey) {
        const ph = this.phone || this.userService.getUser()?.phone || "";
        (window as any).fbq("trackCustom", "ArastirmaTamamlandi", {
          phone: ph,
          key: this.landingPageKey
        });
      }
    } catch (ex) {
      console.log(ex);
    }
  }

  sendVerifyCodeToPhone(again = false) {
    if (this.phoneSmsSent && !again) {
      return;
    }

    if (!this.isUserExistsWithPhone) {
      this.verificationInterval = setInterval(() => {
        const now = new Date();
        this.verificationSmsSecond =
          60 -
          parseInt(
            ((now.getTime() - this.verifySentDate.getTime()) / 1000).toString()
          );
      }, 1000);

      this.http
        .post("/send-verify-code-to-phone", {
          phone: this.phone
        })
        .pipe(
          take(1),
          finalize(() => {
            setTimeout(() => (this.verificationSending = false), 1000);
          })
        )
        .subscribe((res: any) => {
          this.phoneSmsSent = !!(res.alreadySent || res.sent);
          if (this.phoneSmsSent) {
            this.verifySentDate = new Date();
          }
          if (!res.alreadySent && !res.sent) {
            alert(res.message);
          }
        });
    }
  }

  hideResetPassword(result) {
    this.showResetPassword = false;

    if (!result || !result.resetStatus) {
      return;
    }

    this.phonePass = result.password;
    this.phone = result.phone;
    this.phoneCorrect = verifyPhoneNumber(this.phone, false);
    this.finishSurvey();
  }

  closeSuccessDialog() {
    this.state = "";
    this.returnHome();
  }

  labelHtml(option) {
    if (this.landingPageKey) {
      return option.text;
    }
    return `${option.value} ) ${option.text}`;
  }

  fixWithOther(text) {
    return text === "Diğer" ? "Belirtiniz!" : text;
  }

  navigateToThanksPage(saveRes: any) {
    const searchRemovedUrl = this.router.url.replace(
      window.location.search,
      ""
    );
    const url = searchRemovedUrl.endsWith("/")
      ? `${searchRemovedUrl}tesekkurler`
      : `${searchRemovedUrl}/tesekkurler`;

    localStorage.setItem(
      url,
      JSON.stringify({
        landingPageKey: this.landingPageKey,
        discountCode: this.discountCode,
        earningId: saveRes.data.earningId
      })
    );
    window.location.href = url;
    //this.router.navigateByUrl(url);
  }

  openEndedChildOfOption(option, questionIndex) {
    if (option.text !== "Diğer" && option.text !== "Belirtiniz!") {
      return null;
    }

    for (let i = 0; i < this.survey.questions.length; i++) {
      const q = this.survey.questions[i];
      if (
        q.connectedQuestionIndex?.toString() === questionIndex?.toString() &&
        q.connectionOptions?.some((m) => m === option.value) &&
        q.kind == "open"
      ) {
        return q;
      }
    }

    if (!this.otherResults[questionIndex]) {
      this.otherResults[questionIndex] = {
        selected: false,
        value: ""
      };
    }

    return this.survey.questions[questionIndex];
  }

  openEndedChildIndexOfOption(option, questionIndex) {
    const q = this.openEndedChildOfOption(option, questionIndex);
    return this.survey.questions.indexOf(q);
  }

  getOpenEndedChildValue(option, questionIndex) {
    const idx = this.openEndedChildIndexOfOption(option, questionIndex);
    if (this.otherResults[idx]) {
      return this.otherResults[idx].value;
    }
    return this.results[idx];
  }

  handleOpenEndedChildChange(option, questionIndex, evt) {
    // Save question index to scroll to after new questions appear
    const idx = this.openEndedChildIndexOfOption(option, questionIndex);
    this.saveQuestionIndexForScroll(questionIndex);

    if (this.otherResults[idx]) {
      this.otherResults[idx].value = evt.target.value;
    } else {
      this.results[idx] = evt.target.value;
    }
  }

  isResultSelected(option, questionIndex) {
    const res = this.results[questionIndex];
    let selected = false;
    if (typeof res === "number") {
      selected = res == option.value;
    }

    selected = selected || res?.indexOf?.(option.value) > -1;

    if (
      this.openEndedChildOfOption(option, questionIndex) &&
      this.otherResults[questionIndex]
    ) {
      this.otherResults[questionIndex].selected = selected;
    }
    return selected;
  }

  generateInputId(questionIndex, option) {
    const optId =
      typeof option.realIndex === "undefined" ? option.value : option.realIndex;
    return `question${questionIndex}option${optId}`;
  }

  isOptionDisabled(option, questionIndex) {
    const opts = this.survey.questions[questionIndex].question.options;
    const disabledOptionValue = 1;
    const shouldBeDisabled =
      this.isKolektifHouse &&
      opts.some((m) => m.text === "Böyle bir kullanım hiç yapmadım") &&
      Array.isArray(this.results[questionIndex]) &&
      this.results[questionIndex].some((m) => m === disabledOptionValue);

    if (shouldBeDisabled && this.results[questionIndex].length > 1) {
      this.results[questionIndex] = [disabledOptionValue];
    }

    return option.value !== 1 && shouldBeDisabled;
  }

  getRangeOptLabel(option, rangeOpt, rangeIdx) {
    return option.rangeLabels?.[rangeIdx] || rangeOpt.label || rangeOpt.value;
  }

  isNotEmpty(text) {
    return text && text.trim();
  }
}
